import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean indicating whether the element has entered the viewport.
 * Use this to defer rendering of heavy below-the-fold sections.
 * rootMargin controls how far before the element enters the viewport to trigger.
 */
export function useLazySection<T extends Element = HTMLDivElement>(
  rootMargin = '400px'
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, visible];
}
